import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Edit,
  Eye,
  HelpCircle,
  LogOut,
  Mail,
  MousePointer,
} from 'lucide-react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import {
  clearDraftEmailsSuccess,
  clearExportDraftState,
  exportDraftEmailsCSVRequest,
  getDraftEmailsRequest,
  selectDraftEmailsData,
  selectDraftEmailsPagination,
  selectExportDraftBlob,
  selectExportDraftError,
  selectExportDraftLoading,
  selectExportDraftSuccess,
  selectSendEmailError,
  selectSendEmailLoading,
  selectSendEmailMessage,
  selectSendEmailSuccess,
  selectSendAllEmailError,
  selectSendAllEmailLoading,
  selectSendAllEmailSuccess,
  sendAllEmailRequest,
  sendEmailRequest,
} from '../../store/emailSentHistory';
import {
  EmailSentHistoryFilters,
  IEmailSentHistory,
} from '../../types/emailSentHistory/emailSentHistory.types';
import { cn } from '../../utils/cn';
import DateRangePicker from '../ui/DateRangePicker';

interface DraftMessagesProps {
  campaignId: string;
  campaignName?: string;
}

const DraftMessages: React.FC<DraftMessagesProps> = ({
  campaignId,
  campaignName,
}) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { draftEmails, draftLoading, draftError, draftSuccess } = useSelector(
    selectDraftEmailsData
  );
  const pagination = useSelector(selectDraftEmailsPagination);
  const sendLoading = useSelector(selectSendEmailLoading);
  const sendError = useSelector(selectSendEmailError);
  const sendSuccess = useSelector(selectSendEmailSuccess);
  const sendMessage = useSelector(selectSendEmailMessage);
  const sendAllLoading = useSelector(selectSendAllEmailLoading);
  const sendAllError = useSelector(selectSendAllEmailError);
  const sendAllSuccess = useSelector(selectSendAllEmailSuccess);
  const exportBlob = useSelector(selectExportDraftBlob);
  const exportLoading = useSelector(selectExportDraftLoading);
  const exportError = useSelector(selectExportDraftError);
  const exportSuccess = useSelector(selectExportDraftSuccess);

  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized && pagination) {
      const hasPaginationData =
        pagination.page > 0 || pagination.limit !== 10 || pagination.total > 0;

      if (hasPaginationData) {
        if (currentPage !== pagination.page || pageSize !== pagination.limit) {
          setCurrentPage(pagination.page);
          setPageSize(pagination.limit);
        }
      }
      setIsInitialized(true);
    }
  }, [pagination, isInitialized, currentPage, pageSize]);

  // Reset pagination when campaignId changes
  useEffect(() => {
    if (isInitialized) {
      setCurrentPage(0);
      setSelectedEmails(new Set());
    }
  }, [campaignId, isInitialized]);

  // Reset to first page when page size changes
  useEffect(() => {
    if (isInitialized) {
      setCurrentPage(0);
    }
  }, [pageSize, isInitialized]);

  // Fetch draft emails when component mounts or campaignId changes
  useEffect(() => {
    if (isInitialized && campaignId) {
      const filters: EmailSentHistoryFilters = {
        dateRange:
          dateRange.startDate && dateRange.endDate
            ? {
                start: moment(dateRange.startDate).format('YYYY-MM-DD'),
                end: moment(dateRange.endDate).format('YYYY-MM-DD'),
              }
            : undefined,
        page: currentPage,
        limit: pageSize,
      };
      dispatch(getDraftEmailsRequest(campaignId, filters));
      setLastUpdated(new Date());
    }
  }, [dispatch, campaignId, dateRange, currentPage, pageSize, isInitialized]);

  // Clear success message when component unmounts
  useEffect(() => {
    return () => {
      if (draftSuccess) {
        dispatch(clearDraftEmailsSuccess());
      }
      if (exportSuccess) {
        dispatch(clearExportDraftState());
      }
    };
  }, [dispatch, draftSuccess, exportSuccess]);

  // Handle export success
  useEffect(() => {
    if (exportBlob) {
      const url = window.URL.createObjectURL(exportBlob);
      const link = document.createElement('a');
      link.href = url;
      const dateRangeStr =
        dateRange.startDate && dateRange.endDate
          ? `_${moment(dateRange.startDate).format('YYYY-MM-DD')}_${moment(dateRange.endDate).format('YYYY-MM-DD')}`
          : '';
      link.download = `draft_emails${dateRangeStr}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast({
        type: 'success',
        title: 'Export Successful',
        message: 'Draft emails have been exported successfully.',
      });

      dispatch(clearExportDraftState());
    }
  }, [exportBlob, dateRange, showToast, dispatch]);

  // Handle export error
  useEffect(() => {
    if (exportError) {
      showToast({
        type: 'error',
        title: 'Export Failed',
        message: exportError,
      });
      dispatch(clearExportDraftState());
    }
  }, [exportError, showToast, dispatch]);

  // Handle send email success/error feedback
  useEffect(() => {
    if (sendSuccess && sendMessage) {
      if (campaignId) {
        const filters: EmailSentHistoryFilters = {
          dateRange:
            dateRange.startDate && dateRange.endDate
              ? {
                  start: moment(dateRange.startDate).format('YYYY-MM-DD'),
                  end: moment(dateRange.endDate).format('YYYY-MM-DD'),
                }
              : undefined,
          page: currentPage,
          limit: pageSize,
        };
        dispatch(getDraftEmailsRequest(campaignId, filters));
      }
    }
    if (sendError) {
      console.error('Failed to send email:', sendError);
    }
  }, [
    sendSuccess,
    sendError,
    sendMessage,
    dispatch,
    campaignId,
    dateRange,
    currentPage,
    pageSize,
  ]);

  // Helper function to determine status from draft email record
  const getDraftStatus = (draftEmail: IEmailSentHistory): string => {
    if (draftEmail.error) return 'failed';
    if (draftEmail.complaintAt) return 'complaint';
    if (draftEmail.unsubscribedAt) return 'unsubscribed';
    if (draftEmail.spamAt) return 'spam';
    if (draftEmail.bouncedAt) return 'bounced';
    if (draftEmail.clickedEvents && draftEmail.clickedEvents.length > 0)
      return 'clicked';
    if (
      draftEmail.humanOpenedAt ||
      (draftEmail.openedEvents && draftEmail.openedEvents.length > 0)
    )
      return 'opened';
    if (draftEmail.deliveredAt) return 'delivered';
    if (draftEmail.sentAt) return 'sent';
    return 'draft';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
      case 'complaint':
        return 'bg-warning-background text-warning border-warning/30';
      case 'sent':
      case 'delivered':
        return 'bg-success-background text-success border-success/30';
      case 'opened':
        return 'bg-primary-background text-primary border-primary/30';
      case 'clicked':
        return 'bg-info-background text-info border-info/30';
      case 'bounced':
        return 'bg-destructive-background text-destructive border-destructive/30';
      case 'unsubscribed':
        return 'bg-warning-background text-warning border-warning/30';
      case 'spam':
        return 'bg-destructive-background text-destructive border-destructive/30';
      case 'failed':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
        return <Edit className="w-3.5 h-3.5" />;
      case 'sent':
      case 'delivered':
        return <CheckCircle2 className="w-3.5 h-3.5" />;
      case 'opened':
        return <Eye className="w-3.5 h-3.5" />;
      case 'clicked':
        return <MousePointer className="w-3.5 h-3.5" />;
      case 'bounced':
      case 'complaint':
      case 'spam':
        return <AlertCircle className="w-3.5 h-3.5" />;
      case 'unsubscribed':
        return <LogOut className="w-3.5 h-3.5" />;
      case 'failed':
        return <AlertCircle className="w-3.5 h-3.5" />;
      default:
        return <Clock className="w-3.5 h-3.5" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'sent':
        return 'Sent';
      case 'delivered':
        return 'Delivered';
      case 'opened':
        return 'Opened';
      case 'clicked':
        return 'Clicked';
      case 'bounced':
        return 'Bounced';
      case 'complaint':
        return 'Complaint';
      case 'unsubscribed':
        return 'Unsubscribed';
      case 'spam':
        return 'Spam';
      case 'failed':
        return 'Failed';
      default:
        return 'Pending';
    }
  };

  // Helper function to format date using moment in UTC
  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return 'N/A';

    const momentDate = moment(date);
    const now = moment();

    if (!momentDate.isValid()) return 'Invalid date';

    if (momentDate.isAfter(now.subtract(1, 'hour'))) {
      return momentDate.fromNow();
    }

    if (momentDate.isSame(now, 'day')) {
      return `Today at ${momentDate.format('h:mm A')}`;
    }

    if (momentDate.isSame(now.subtract(1, 'day'), 'day')) {
      return `Yesterday at ${momentDate.format('h:mm A')}`;
    }

    if (momentDate.isSame(now, 'year')) {
      return momentDate.format('MMM D, h:mm A');
    }

    return momentDate.format('MMM D, YYYY h:mm A');
  };

  // Helper function to get action text
  const getActionText = (draftEmail: IEmailSentHistory): string => {
    const status = getDraftStatus(draftEmail);
    switch (status) {
      case 'draft':
        return 'Email draft created';
      case 'clicked':
        return `Email clicked (${draftEmail.clickedEvents?.length || 0} times)`;
      case 'opened':
        return `Email opened (${draftEmail.openedEvents?.length || 0} times)`;
      case 'bounced':
        return 'Email bounced';
      case 'complaint':
        return 'Complaint received';
      case 'unsubscribed':
        return 'Contact unsubscribed';
      case 'spam':
        return 'Marked as spam';
      case 'failed':
        return 'Failed to send';
      case 'delivered':
        return 'Email delivered';
      default:
        return 'Email sent';
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  // Check if row is expanded
  const isRowExpanded = (rowId: string) => {
    return expandedRows.has(rowId);
  };

  // Helper function to get campaign name
  const getCampaignName = (): string => {
    return campaignName || 'Untitled Campaign';
  };

  // Helper functions for selection
  const toggleEmailSelection = (emailId: string) => {
    setSelectedEmails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };

  const isEmailSelected = (emailId: string) => {
    return selectedEmails.has(emailId);
  };

  // Handle sending individual email
  const handleSendEmail = (emailId: string) => {
    dispatch(sendEmailRequest(emailId));
  };

  const handleSendAllEmail = () => {
    showToast({
      type: 'info',
      title: 'Sending Started',
      message: 'Emails will be sent to users shortly.',
    });
    dispatch(sendAllEmailRequest(campaignId));
  };

  const formatLastUpdated = () => {
    return moment(lastUpdated).format('MMM D, YYYY [at] h:mm:ss A [(IST)]');
  };

  // Handle export
  const handleExport = () => {
    const startDate = dateRange.startDate
      ? moment(dateRange.startDate).format('YYYY-MM-DD')
      : undefined;
    const endDate = dateRange.endDate
      ? moment(dateRange.endDate).format('YYYY-MM-DD')
      : undefined;
    dispatch(exportDraftEmailsCSVRequest(campaignId, startDate, endDate));
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Clear selected emails when changing pages
    setSelectedEmails(new Set());
  };

  useEffect(() => {
    if (sendAllSuccess && campaignId) {
      const filters: EmailSentHistoryFilters = {
        dateRange:
          dateRange.startDate && dateRange.endDate
            ? {
                start: moment(dateRange.startDate).format('YYYY-MM-DD'),
                end: moment(dateRange.endDate).format('YYYY-MM-DD'),
              }
            : undefined,
        page: currentPage,
        limit: pageSize,
      };
      dispatch(getDraftEmailsRequest(campaignId, filters));
    }

    if (sendAllError) {
      showToast({
        type: 'error',
        title: 'Send All Failed',
        message: sendAllError,
      });
    }
  }, [
    sendAllSuccess,
    sendAllError,
    campaignId,
    dateRange,
    currentPage,
    pageSize,
    dispatch,
    showToast,
  ]);

  return (
    <div className="flex-1">
      {/* Compact Header with Filters */}
      <div className="px-6 pt-4">
        <div className="flex items-center justify-between gap-4 ">
          {/* Left side - Filters */}
          <div className="flex items-center gap-4 flex-1">
            {/* Channel Filter */}
            {/* <div className="relative">
              <select className="appearance-none bg-card border border-border rounded-lg px-4 py-2.5 pr-10 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer min-w-[140px]">
                <option>All channels</option>
                <option>Email</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </div>
            </div> */}

            {/* Date Range */}
            <div className="w-64">
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                placeholder="Select date range"
              />
            </div>
          </div>

          {/* Right side - Status and Actions */}
          <div className="flex items-center gap-4">
            <div className="text-xs text-muted-foreground">
              Last updated {formatLastUpdated()}
            </div>

            {/* <Switch
              label="Auto refresh"
              checked={autoRefresh}
              onChange={setAutoRefresh}
              className="items-center gap-2"
            /> */}

            <button
              type="button"
              onClick={handleSendAllEmail}
              disabled={sendAllLoading || !campaignId}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-success rounded-lg hover:bg-success/90 transition-colors"
            >
              <Mail className="w-4 h-4" />
              <span>{sendAllLoading ? 'Sending...' : 'Send All Email'}</span>
            </button>

            <button
              onClick={handleExport}
              disabled={exportLoading}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground bg-muted/50 rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              <span>{exportLoading ? 'Exporting...' : 'Export to CSV'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {/* {draftEmails.length > 0 && (
        <div className="flex items-center space-x-3 pt-4 px-4">
          <input
            type="checkbox"
            checked={isAllSelected()}
            onChange={selectAllEmails}
            className="w-4 h-4 ml-4 rounded border-border accent-primary focus:ring-0"
          />

          {selectedEmails.size > 0 ? (
            <>
              <ActionButton
                action="send"
                scope="selected"
                icon={SendIcon}
                className="text-success bg-success-background border border-success/30 rounded-md hover:bg-success-hover"
              />
              <ActionButton
                action="delete"
                scope="selected"
                icon={Trash2}
                className="text-destructive bg-destructive-background border border-destructive/30 rounded-md hover:bg-destructive-hover"
              />
            </>
          ) : (
            <>
              <ActionButton
                action="send"
                scope="all"
                icon={SendIcon}
                className="text-success bg-success-background border border-success/30 rounded-md hover:bg-success-hover"
              />
              <ActionButton
                action="delete"
                scope="all"
                icon={Trash2}
                className="text-destructive bg-destructive-background border border-destructive/30 rounded-md hover:bg-destructive-hover"
              />
            </>
          )}
        </div>
      )} */}

      <div className="mb-4 overflow-x-auto">
        {/* Messages Table */}
        <div className="rounded-xl border border-border mx-6 my-4">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  Date Created
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  Action
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  Recipient / Person
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  <div className="flex items-center gap-1">
                    <span>Status</span>
                    <HelpCircle className="w-3 h-3 text-muted-foreground" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {draftLoading ? (
                <tr>
                  <td colSpan={4} className="py-8 px-4 text-center">
                    <div className="flex items-center justify-center gap-3">
                      <Clock className="w-5 h-5 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground font-medium">
                        Loading draft messages...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : draftError ? (
                <tr>
                  <td colSpan={4} className="py-8 px-4 text-center">
                    <div className="flex items-center justify-center gap-3 text-destructive">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        Error loading draft messages: {draftError}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : draftEmails.length > 0 ? (
                draftEmails.map(
                  (draftEmail: IEmailSentHistory, index: number) => {
                    const emailStatus = getDraftStatus(draftEmail);
                    const actionText = getActionText(draftEmail);
                    const displayDate = formatDate(
                      draftEmail.sentAt || draftEmail.createdAt
                    );
                    const rowId =
                      draftEmail._id || draftEmail.messageId || `row-${index}`;
                    const isExpanded = isRowExpanded(rowId);
                    const displayCampaignName = getCampaignName();
                    const emailId =
                      draftEmail._id ||
                      draftEmail.messageId ||
                      `email-${index}`;

                    return (
                      <React.Fragment key={rowId}>
                        {/* Main Row */}
                        <tr
                          className={cn(
                            'border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer',
                            isEmailSelected(emailId) && 'bg-primary/10'
                          )}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isEmailSelected(emailId)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleEmailSelection(emailId);
                                }}
                                className="w-4 h-4 rounded border-border accent-primary focus:ring-0"
                              />
                              <button
                                onClick={() => toggleRowExpansion(rowId)}
                                className="flex-shrink-0 p-1 hover:bg-muted rounded transition-colors"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                                )}
                              </button>
                              <span className="text-sm text-card-foreground">
                                {displayDate}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                              <span className="text-sm text-card-foreground truncate">
                                {actionText}
                              </span>
                              <ChevronRight className="w-3 h-3 text-muted-foreground" />
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Link
                              to={`/contacts/${draftEmail.contactId}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-2"
                            >
                              <div className="text-sm text-primary hover:text-primary-hover cursor-pointer hover:underline truncate">
                                {draftEmail.email}
                              </div>
                              <ChevronRight className="w-3 h-3 text-muted-foreground" />
                            </Link>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
                                getStatusColor(emailStatus)
                              )}
                            >
                              {getStatusIcon(emailStatus)}
                              <span>{getStatusText(emailStatus)}</span>
                            </span>
                          </td>
                        </tr>

                        {/* Expanded Row */}
                        {isExpanded && (
                          <tr className="bg-muted/30">
                            <td colSpan={4} className="px-4 py-3">
                              <div className="pl-6 space-y-3">
                                {/* Campaign Information */}
                                <div className="flex items-center gap-4">
                                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide min-w-[120px]">
                                    TRIGGERED CAMPAIGN:
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <button className="text-sm text-primary hover:text-primary-hover font-medium transition-colors">
                                      {displayCampaignName}
                                    </button>
                                    <ChevronRight className="w-3 h-3 text-muted-foreground" />
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    Created {formatDate(draftEmail.createdAt)}
                                  </span>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center gap-2 pt-1">
                                  {emailStatus === 'draft' && (
                                    <button
                                      onClick={() => handleSendEmail(emailId)}
                                      disabled={sendLoading}
                                      className="px-3 py-1 text-xs font-medium text-white bg-success border border-success rounded-lg hover:bg-success/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {sendLoading ? 'Sending...' : 'Send'}
                                    </button>
                                  )}
                                  {/* <button className="px-3 py-1 text-xs font-medium text-destructive bg-destructive-background border border-destructive/30 rounded-lg hover:bg-destructive-background/80 transition-colors">
                                    Delete
                                  </button> */}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }
                )
              ) : (
                <tr>
                  <td colSpan={4} className="py-12 px-4 text-center">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
                        <Edit className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <h3 className="text-sm font-semibold text-card-foreground mb-1">
                        No draft messages
                      </h3>
                      <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                        No draft messages have been created yet for this
                        campaign.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {!draftLoading && !draftError && draftEmails.length > 0 && (
            <div className="px-4 py-3 border-t border-border bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-muted-foreground">
                    {pagination.page * pagination.limit + 1}-
                    {Math.min(
                      (pagination.page + 1) * pagination.limit,
                      pagination.total
                    )}{' '}
                    of {pagination.total}
                  </div>

                  {/* Page Limit Dropdown */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Show:</span>
                    <div className="relative">
                      <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="appearance-none bg-card border border-border rounded-lg px-2 py-1 pr-8 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      per page
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    className={`p-2 rounded border rounded-lg border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${pagination.page === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={pagination.page === 0}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <span className="text-sm text-muted-foreground px-3 py-1">
                    Page {pagination.page + 1} of {pagination.totalPages}
                  </span>

                  <button
                    className={`p-2 rounded border rounded-lg border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${pagination.page >= pagination.totalPages - 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={pagination.page >= pagination.totalPages - 1}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DraftMessages;
